import React, { useEffect, useRef, useState, useMemo } from 'react';
import { Category } from '../../services/categoryService';
import { Tour } from '../../types';
import { useTheme } from '../../contexts/ThemeContext';

interface CircularCardWheelProps {
    items: (Category | Tour)[];
    mode: 'categories' | 'tours';
    categoryImages?: Record<string, string>;
    onResult: (item: Category | Tour) => void;
    isSpinning: boolean; // Kept for prop compatibility, but internal state controls flow now
    onSpinComplete: () => void;
    // New prop to trigger spin from inside component
    onStartSpin?: () => void;
}

type Phase = 'IDLE' | 'DEPLOYING' | 'SPINNING' | 'SLOWING' | 'REVEALING' | 'SHOWCASE';

interface CardState {
    id: string;
    x: number;
    y: number;
    rotation: number;
    scale: number;
    opacity: number;
    zIndex: number;
    targetRx: number; // Orbital Radius X
    targetRy: number; // Orbital Radius Y
    angle: number;    // Current orbital angle
    speed: number;    // Individual rotation speed offset
}

const CircularCardWheel: React.FC<CircularCardWheelProps> = ({
    items,
    mode,
    categoryImages,
    onResult,
    onSpinComplete,
    onStartSpin
}) => {
    const { actualTheme } = useTheme();
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

    // Update mobile state on resize
    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const baseSize = isMobile ? 600 : 1000; // Smaller logical grid for mobile
    const cardWidth = isMobile ? 90 : 140;
    const cardHeight = isMobile ? 120 : 180;

    // Orbit radii - Spread them out more for mobile to fill the circumference
    const orbits = isMobile
        ? [160, 250, 340]
        : [220, 320, 420];

    const [phase, setPhase] = useState<Phase>('IDLE');
    const [scale, setScale] = useState(1);
    const containerRef = useRef<HTMLDivElement>(null);

    // Physics State
    const cardsRef = useRef<CardState[]>([]);
    const animationRef = useRef<number | null>(null);
    const globalRotationRef = useRef(0);
    const globalSpeedRef = useRef(0);
    const timeRef = useRef(0);
    const winnerIndexRef = useRef(-1);

    // Initialize Card States
    useEffect(() => {
        if (items.length === 0) return;

        // Create initial states if not exists or length mismatch
        if (cardsRef.current.length !== items.length) {
            cardsRef.current = items.map((item, index) => {
                // Assign orbit based on index (modulo 3)
                const orbitIndex = index % 3;
                const radius = orbits[orbitIndex];

                // INTERLEAVED ANGULAR DISTRIBUTION
                const totalRingItems = Math.ceil(items.length / 3);
                const slotInRing = Math.floor(index / 3);
                const ringOffset = orbitIndex * (Math.PI * 2 / (totalRingItems * 3));
                const angleInfo = (slotInRing / totalRingItems) * (Math.PI * 2) + ringOffset;

                return {
                    id: 'id' in item ? item.id : `item-${index}`,
                    x: 0,
                    y: 0,
                    rotation: -10 + (index * 0.5),
                    scale: 0.8,
                    opacity: 1,
                    zIndex: index,
                    targetRx: radius,
                    targetRy: radius * 0.9,
                    angle: angleInfo,
                    speed: 0.8 + (Math.random() * 0.4)
                };
            });
        }
    }, [items, isMobile, orbits]);

    // Resize Handler - Scale based on Container Size
    useEffect(() => {
        if (!containerRef.current) return;

        const observer = new ResizeObserver((entries) => {
            const entry = entries[0];
            if (entry) {
                const { width, height } = entry.contentRect;

                // We built the physics for a 1000x1000 logical grid.
                // We simply scale that grid to fit the container.
                const size = Math.min(width, height);
                // Avoid divide by zero
                const safeSize = size > 0 ? size : 100;

                // Scale factor: Container Size / Logical Base Size
                const newScale = (safeSize / baseSize) * 0.95;

                setScale(newScale);
            }
        });

        observer.observe(containerRef.current);
        return () => observer.disconnect();
    }, [baseSize]);

    // Animation Loop
    useEffect(() => {
        const animate = () => {
            timeRef.current += 0.016; // Approx 60fps

            // UPDATE GLOBAL PHYSICS BASED ON PHASE
            if (phase === 'DEPLOYING') {
                // Move from stack to orbit
                // Lerp global speed up
                globalSpeedRef.current += (0.05 - globalSpeedRef.current) * 0.05;

                let allDeployed = true;
                cardsRef.current.forEach((card) => {
                    const targetX = Math.cos(card.angle + globalRotationRef.current) * card.targetRx;
                    const targetY = Math.sin(card.angle + globalRotationRef.current) * card.targetRy;

                    // Smooth lerp to orbit position
                    card.x += (targetX - card.x) * 0.08;
                    card.y += (targetY - card.y) * 0.08;

                    // Rotate to upright
                    card.rotation += (0 - card.rotation) * 0.1;

                    if (Math.abs(card.x - targetX) > 5) allDeployed = false;
                });

                if (allDeployed && timeRef.current > 1.0) {
                    setPhase('SPINNING');
                }
            }
            else if (phase === 'SPINNING') {
                // SLOW SPIN: Target 0.08
                globalSpeedRef.current += (0.08 - globalSpeedRef.current) * 0.02;
                globalRotationRef.current += globalSpeedRef.current;

                // Orbital movement
                cardsRef.current.forEach((card, i) => {
                    // Unique orbital speed for chaos
                    const orbitalAngle = card.angle + globalRotationRef.current * (1 + (i % 2 === 0 ? 0.1 : -0.1));

                    card.x = Math.cos(orbitalAngle) * card.targetRx;
                    card.y = Math.sin(orbitalAngle) * card.targetRy;

                    // Tilt cards based on movement (tangent)
                    // At high speeds, tilt nicely
                    card.rotation = Math.cos(orbitalAngle) * 15;
                });

                // NO AUTO STOP - User must click
            }
            else if (phase === 'SLOWING') {
                // Deccelerate
                globalSpeedRef.current *= 0.96; // Friction
                globalRotationRef.current += globalSpeedRef.current;

                // Pick winner if not selected
                if (winnerIndexRef.current === -1 && cardsRef.current.length > 0) {
                    winnerIndexRef.current = Math.floor(Math.random() * items.length);
                }

                cardsRef.current.forEach((card, i) => {
                    const orbitalAngle = card.angle + globalRotationRef.current;
                    card.x = Math.cos(orbitalAngle) * card.targetRx;
                    card.y = Math.sin(orbitalAngle) * card.targetRy;
                    card.rotation = Math.cos(orbitalAngle) * 5;
                });

                if (globalSpeedRef.current < 0.002) {
                    setPhase('REVEALING');
                    // Ensure winner is set
                    if (winnerIndexRef.current === -1) winnerIndexRef.current = Math.floor(Math.random() * items.length);
                    // Pass to parent
                    onResult(items[winnerIndexRef.current]);
                }
            }
            else if (phase === 'REVEALING') {
                const winnerIdx = winnerIndexRef.current;
                const winner = cardsRef.current[winnerIdx];

                if (winner) {
                    // Move winner to center
                    winner.x += (0 - winner.x) * 0.1;
                    winner.y += (0 - winner.y) * 0.1;
                    winner.scale += (1.5 - winner.scale) * 0.05;
                    winner.rotation += (0 - winner.rotation) * 0.1;
                    winner.zIndex = 100;

                    // Explode others
                    cardsRef.current.forEach((card, i) => {
                        if (i !== winnerIdx) {
                            const angle = Math.atan2(card.y, card.x);
                            // Push away
                            card.x += Math.cos(angle) * 15;
                            card.y += Math.sin(angle) * 15;
                            card.opacity *= 0.92;
                        }
                    });

                    if (Math.abs(winner.x) < 1 && Math.abs(winner.y) < 1) {
                        // Done
                        setPhase('SHOWCASE');
                        setTimeout(() => onSpinComplete(), 200);
                    }
                }
            }

            // Apply to DOM
            if (containerRef.current) {
                cardsRef.current.forEach((card, i) => {
                    const el = document.getElementById(`card-` + i);
                    if (el) {
                        el.style.transform = `translate(${card.x}px, ${card.y}px) rotate(${card.rotation}deg) scale(${card.scale})`;
                        el.style.opacity = card.opacity.toString();
                        el.style.zIndex = card.zIndex.toString();
                    }
                });
            }

            if (phase !== 'SHOWCASE') {
                animationRef.current = requestAnimationFrame(animate);
            }
        };

        if (phase !== 'IDLE' && phase !== 'SHOWCASE') {
            animationRef.current = requestAnimationFrame(animate);
        }

        return () => {
            if (animationRef.current) cancelAnimationFrame(animationRef.current);
        };
    }, [phase, items]); // onSpinComplete and onResult omitted from deps to avoid re-triggering animation on parent render

    // Handle Start / Stop
    const handleHubClick = () => {
        if (phase === 'IDLE') {
            if (onStartSpin) onStartSpin();
            setPhase('DEPLOYING');
            timeRef.current = 0;
            globalSpeedRef.current = 0;
            globalRotationRef.current = 0;
            winnerIndexRef.current = -1;
        } else if (phase === 'SPINNING') {
            setPhase('SLOWING');
        }
    };

    const getItemImage = (item: Category | Tour) => {
        if ('title' in item) { // Is Tour
            return item.images && item.images.length > 0 ? item.images[0] : '';
        } else { // Is Category
            return categoryImages && categoryImages[item.id] ? categoryImages[item.id] : '';
        }
    };

    const getColor = (item: Category | Tour) => {
        return !('title' in item) && 'color' in item ? item.color : '#0ea5e9';
    };

    return (
        <div
            ref={containerRef}
            className="relative w-full h-full flex items-center justify-center perspective-[2000px]"
        >
            {/* Animation Stage Wrapper - Centered perfectly with absolute positioning */}
            <div
                className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 transform-gpu"
                style={{
                    transformOrigin: 'center center',
                    transform: `translate(-50%, -50%) scale(${scale})`,
                    width: `${baseSize}px`, // Explicit logical size
                    height: `${baseSize}px`
                }}
            >
                {/* Orbital Rings - Visual Guide (Enforced Light Mode) */}
                <div className={`absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full border border-dashed border-slate-300 transition-all duration-1000 ${phase === 'IDLE' ? 'opacity-0 scale-50' : 'opacity-40 scale-100'}`} style={{ width: isMobile ? 320 : 440, height: isMobile ? 320 : 440 }} />
                <div className={`absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full border border-dashed border-slate-200 transition-all duration-1000 ${phase === 'IDLE' ? 'opacity-0 scale-50' : 'opacity-30 scale-100'}`} style={{ width: isMobile ? 500 : 640, height: isMobile ? 500 : 640 }} />
                <div className={`absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full border border-dashed border-slate-100 transition-all duration-1000 ${phase === 'IDLE' ? 'opacity-0 scale-50' : 'opacity-20 scale-100'}`} style={{ width: isMobile ? 680 : 840, height: isMobile ? 680 : 840 }} />

                {/* Cards */}
                {items.map((item, index) => {
                    const image = getItemImage(item);
                    const color = getColor(item);

                    const isIdle = phase === 'IDLE';
                    // Center the stack perfectly on center hub for mobile
                    const stackX = isMobile ? 0 : -350 + (index * 2);
                    const stackY = isMobile ? 0 : -250 + (index * 2);
                    const stackRot = -5 + (index * 1);

                    return (
                        <div
                            id={`card-` + index}
                            key={index}
                            className={`absolute left-1/2 top-1/2 rounded-xl shadow-2xl overflow-hidden border-2 border-white/50 bg-white/20 backdrop-blur-sm
                                ${isIdle ? 'transition-all duration-500 ease-out' : ''} 
                            `}
                            style={{
                                width: `${cardWidth}px`,
                                height: `${cardHeight}px`,
                                marginLeft: `-${cardWidth / 2}px`,
                                marginTop: `-${cardHeight / 2}px`,
                                transform: isIdle
                                    ? `translate(${stackX}px, ${stackY}px) rotate(${stackRot}deg) scale(0.8)`
                                    : undefined,
                                zIndex: index,
                                boxShadow: '0 10px 40px -10px rgba(0,0,0,0.3)',
                                opacity: 1
                            }}
                        >
                            {image ? (
                                <img src={image} className="w-full h-full object-cover" alt="" draggable={false} />
                            ) : (
                                <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-slate-200/50 to-slate-400/50">
                                    <span className="text-4xl font-black text-white/40">?</span>
                                </div>
                            )}
                        </div>
                    );
                })}

                {/* Central Hub - The Interaction Point */}
                <div
                    className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-[200] cursor-pointer"
                    onClick={handleHubClick}
                    style={{ transform: phase === 'IDLE' ? 'translate(-50%, -50%) scale(1)' : 'translate(-50%, -50%) scale(0.8)' }}
                >
                    {/* Pulse Effect */}
                    <div className={`absolute inset-0 bg-primary-400 rounded-full ${phase === 'IDLE' ? 'animate-ping opacity-20' : 'opacity-0'}`} />

                    {/* Main Orb - Enforced Light Theme with Blue/Amber highlight */}
                    <div
                        className={`
                            relative w-36 h-36 rounded-full 
                            bg-white/40 backdrop-blur-2xl border-2 border-white/60
                            flex items-center justify-center shadow-[0_20px_50px_rgba(0,0,0,0.15)]
                            transition-all duration-300 hover:scale-110 active:scale-95 group
                        `}
                    >
                        {/* Spinning rings inside hub */}
                        <div className={`absolute inset-3 rounded-full border-2 border-primary-500/10 ${phase === 'SPINNING' ? 'animate-[spin_0.5s_linear_infinite]' : 'animate-[spin_4s_linear_infinite]'}`} />
                        <div className={`absolute inset-2 rounded-full border border-dashed border-primary-400/20 ${phase === 'SPINNING' ? 'animate-[spin_1s_linear_infinite_reverse]' : 'animate-[spin_10s_linear_infinite_reverse]'}`} />

                        <div className="flex flex-col items-center">
                            <span className={`
                                font-black tracking-[0.3em] text-transparent bg-clip-text bg-gradient-to-r from-primary-700 to-indigo-800
                                transition-all duration-300 uppercase
                                ${phase === 'SPINNING' || phase === 'SLOWING' ? 'text-2xl' : 'text-sm'}
                            `}>
                                {phase === 'IDLE' ? 'EXPLORE' : (phase === 'SPINNING' || phase === 'SLOWING') ? 'STOP' : phase === 'SHOWCASE' ? 'REVEAL' : 'SAFARI'}
                            </span>
                            {phase === 'IDLE' && (
                                <span className="text-[10px] font-bold text-primary-900/40 tracking-widest mt-1">SPIN WHEEL</span>
                            )}
                        </div>
                    </div>
                </div>

            </div>
        </div>
    );
};

export default CircularCardWheel;
